import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

const useResizeObserver = (ref) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      });
    });

    resizeObserver.observe(observeTarget);

    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);

  return dimensions;
};

interface DAMP_VIS_CIR_Props {
  maxIterations?: number;
}

const CircleContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const Circle = styled(Box)<{ size: number; maxSize: number; maxIterations: number }>(
  ({ size, maxSize, maxIterations, theme }) => ({
    width: `${size * maxSize}px`,
    height: `${size * maxSize}px`,
    backgroundColor: "#ff0000",
    borderRadius: "50%",
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: 'all 0.3s ease-in-out',
  })
);

const Number = styled(Typography)<{
  position: "top" | "right" | "bottom" | "left";
  size: number;
  maxIterations: number;
}>(({ position, size, maxIterations, theme }) => {
  const getPosition = () => {
    const baseOffset = 20;
    const dynamicOffset = baseOffset + (size / maxIterations) * 30; // Ajusta este valor según sea necesario
    const offset = `${dynamicOffset}px`;
    switch (position) {
      case "top":
        return {
          top: `-${offset}`,
          left: "50%",
          transform: "translateX(-50%)",
        };
      case "right":
        return {
          top: "50%",
          right: `-${offset}`,
          transform: "translateY(-50%)",
        };
      case "bottom":
        return {
          bottom: `-${offset}`,
          left: "50%",
          transform: "translateX(-50%)",
        };
      case "left":
        return {
          top: "50%",
          left: `-${offset}`,
          transform: "translateY(-50%)",
        };
    }
  };

  return {
    position: "absolute",
    ...getPosition(),
    fontSize: `${Math.max(12, size * 3)}px`,
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      fontSize: `${Math.max(10, size * 3 * 0.8)}px`,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: `${Math.max(8, size * 3 * 0.6)}px`,
    },
  };
});

export default function DAMP_VIS_CIR({ exercise, isRunning, velocity }) {
  const [currentSize, setCurrentSize] = useState(1);
  const theme = useTheme();
  const [maxIterations, setMaxIterations] = useState(exercise.lineas)
  const isXSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const containerRef = useRef(null);
  const { width: containerWidth, height: containerHeight } = useResizeObserver(containerRef);

  const parentStyle = {
    position: "relative",
    width: "100%",
    height: "60vh",
  };

  const getGapSize = (currentSize: number) => {
    const baseGap = 10;
    const maxGap = 50;
    const gap = baseGap + (maxGap - baseGap) * (currentSize / maxIterations);
    return {
      xs: `${gap * 0.5}px`,
      sm: `${gap * 0.75}px`,
      md: `${gap}px`,
      lg: `${gap * 1.25}px`,
    };
  };

  const getMaxSize = () => {
    const minDimension = Math.min(containerWidth, containerHeight);
    const baseSize = minDimension / (maxIterations * 2); // Dividimos por 2 para dejar espacio para los números
    return Math.max(10, baseSize); // Asegura un tamaño mínimo de 10px
  };

  const maxSize = getMaxSize();

  useEffect(() => {
    let timer = null
    if (isRunning) {
      timer = setTimeout(() => {
        setCurrentSize((prev) => (prev >= maxIterations ? 1 : prev + 1));
      }, 1000);
    }
    return () => { if (timer) clearTimeout(timer) };
  }, [currentSize, maxIterations, isRunning]);

  return (
    <Box sx={parentStyle} ref={containerRef}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: getGapSize(currentSize),
          position: "absolute",
          top: 0,
          left: 0,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          background: "#ffffff",
        }}
      >
        <CircleContainer>
          <Circle size={currentSize} maxSize={maxSize} maxIterations={maxIterations}>
            <Number position="top" size={currentSize} maxIterations={maxIterations}>
              {currentSize}
            </Number>
            <Number position="right" size={currentSize} maxIterations={maxIterations}>
              {currentSize}
            </Number>
            <Number position="bottom" size={currentSize} maxIterations={maxIterations}>
              {currentSize}
            </Number>
            <Number position="left" size={currentSize} maxIterations={maxIterations}>
              {currentSize}
            </Number>
          </Circle>
        </CircleContainer>
      </Box>
    </Box>
  );
}

